import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { auth } from '../../../services/auth.js';
import { XP_RULES } from '@xdface-vault/shared';
import { useGamificationStore } from '../../../stores/gamification-store.js';
import { courseService } from '../../../services/courses.js';
import type {
  LessonCreateInput,
  LessonUpdateInput,
  LessonResponse,
  LessonProgress,
  HighlightResponse,
  HighlightCreateInput,
  HighlightUpdateInput,
} from '../../../services/courses.js';
import {
  enqueueCourseCreate,
  enqueueCourseDelete,
  enqueueCourseUpdate,
  courseHighlightRef,
  OPTIMISTIC_ID_PREFIX,
} from '../../../lib/offline-queue.js';
import { scheduleUndoableDelete } from '../../../lib/undo-delete.js';
import { COURSE_KEYS } from './useCourses.js';

/** fetch TypeError (network down) or the browser reporting itself offline. */
function isOfflineError(err: unknown): boolean {
  return err instanceof TypeError || !navigator.onLine;
}

// ---------- Lesson queries ----------
export function useCourseLessons(courseId: string | undefined) {
  return useQuery<LessonResponse[]>({
    queryKey: COURSE_KEYS.lessons(courseId ?? ''),
    queryFn: () => courseService.getCourseLessons(courseId!),
    enabled: !!courseId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useLessonContent(courseId: string | undefined, lessonNum: number | null) {
  return useQuery<LessonResponse>({
    queryKey: ['courses', courseId, 'lessons', lessonNum, 'content'],
    queryFn: () => courseService.getLesson(courseId!, lessonNum!),
    enabled: !!courseId && lessonNum !== null,
    staleTime: 1000 * 60 * 55, // presigned URLs last 1h, refresh before expiry
  });
}

// ---------- Lesson mutations ----------
export function useCreateLesson(courseId: string) {
  const qc = useQueryClient();
  return useMutation<LessonResponse, Error, LessonCreateInput>({
    mutationFn: (input) => courseService.createLesson(courseId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.lessons(courseId) });
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

export function useUpdateLesson(courseId: string) {
  const qc = useQueryClient();
  return useMutation<LessonResponse, Error, { lessonNum: number; input: LessonUpdateInput }>({
    mutationFn: ({ lessonNum, input }) => courseService.updateLesson(courseId, lessonNum, input),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.lessons(courseId) });
    },
  });
}

export function useDeleteLesson(courseId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (lessonNum) => courseService.deleteLesson(courseId, lessonNum),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.lessons(courseId) });
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

export function useReorderLessons(courseId: string) {
  const qc = useQueryClient();
  return useMutation<{ reordered: boolean }, Error, number[]>({
    mutationFn: (order) => courseService.reorderLessons(courseId, order),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.lessons(courseId) });
    },
  });
}

// ---------- Lesson progress ----------
export function useLessonProgress(courseId: string | undefined) {
  return useQuery<LessonProgress[]>({
    queryKey: COURSE_KEYS.progress(courseId ?? ''),
    queryFn: async () => {
      const res = await courseService.getCourseProgress(courseId!);
      return res.items;
    },
    enabled: !!courseId,
    staleTime: 1000 * 60,
  });
}

type ProgressCtx = { prev: LessonProgress[] | undefined };

export function useMarkLessonComplete(courseId: string) {
  const qc = useQueryClient();
  const key = COURSE_KEYS.progress(courseId);
  return useMutation<
    { lessonNum: number; completedAt: string; courseCompleted: boolean },
    Error,
    number,
    ProgressCtx
  >({
    mutationFn: (lessonNum) => courseService.markLessonComplete(courseId, lessonNum),
    onMutate: async (lessonNum) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<LessonProgress[]>(key);
      qc.setQueryData<LessonProgress[]>(key, (old = []) =>
        old.some((p) => p.lessonNum === lessonNum)
          ? old
          : [...old, { lessonNum, completedAt: new Date().toISOString() }],
      );
      return { prev };
    },
    onError: (_err, _lessonNum, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: (data) => {
      // Feedback optimista: el XP real lo otorga el backend de forma async.
      // `courseCompleted` solo viene en true en la transición a completado
      // (re-POST idempotente devuelve false y no anticipa XP extra).
      useGamificationStore.getState().addXP(XP_RULES.LESSON_COMPLETED);
      if (data.courseCompleted) {
        useGamificationStore.getState().addXP(XP_RULES.COURSE_COMPLETED);
      }
      qc.invalidateQueries({ queryKey: ['gamification'] });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['progress', 'detail', 'course', courseId] });
    },
  });
}

export function useUnmarkLessonComplete(courseId: string) {
  const qc = useQueryClient();
  const key = COURSE_KEYS.progress(courseId);
  return useMutation<{ lessonNum: number; unmarked: boolean }, Error, number, ProgressCtx>({
    mutationFn: (lessonNum) => courseService.unmarkLessonComplete(courseId, lessonNum),
    onMutate: async (lessonNum) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<LessonProgress[]>(key);
      qc.setQueryData<LessonProgress[]>(key, (old = []) =>
        old.filter((p) => p.lessonNum !== lessonNum),
      );
      return { prev };
    },
    onError: (_err, _lessonNum, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['progress', 'detail', 'course', courseId] });
    },
  });
}

// ---------- Lesson highlights ----------
export function useLessonHighlights(courseId: string | undefined, lessonNum: number | null) {
  return useQuery<HighlightResponse[]>({
    queryKey: COURSE_KEYS.highlights(courseId ?? '', lessonNum ?? 0),
    queryFn: async () => courseService.getLessonHighlights(courseId!, lessonNum!),
    enabled: !!courseId && lessonNum !== null,
    staleTime: 1000 * 60 * 2,
  });
}

type HighlightMutationCtx = { prev?: HighlightResponse[]; queuedOffline?: boolean };

export function useCreateHighlight(courseId: string, lessonNum: number) {
  const qc = useQueryClient();
  const ownerId = useRef(auth.getUserId()).current;
  const key = COURSE_KEYS.highlights(courseId, lessonNum);

  // When the offline queue replays a queued create, swap the optimistic
  // temp-id entry for the saved highlight.
  useEffect(() => {
    const onConfirmed = (e: Event) => {
      const { tempId, highlight } = (
        e as CustomEvent<{ tempId: string; highlight: HighlightResponse }>
      ).detail;
      qc.setQueryData<HighlightResponse[]>(key, (old) =>
        old?.some((h) => h.id === tempId) ? old.map((h) => (h.id === tempId ? highlight : h)) : old,
      );
    };
    window.addEventListener('vault:hl-confirmed', onConfirmed);
    return () => window.removeEventListener('vault:hl-confirmed', onConfirmed);
  }, [qc, key]);

  return useMutation<
    HighlightResponse,
    Error,
    HighlightCreateInput,
    HighlightMutationCtx & { tempId: string }
  >({
    mutationFn: (input) => {
      if (!ownerId || auth.getUserId() !== ownerId) throw new Error('La sesión cambió');
      return courseService.createHighlight(courseId, lessonNum, input, ownerId);
    },
    // Optimistic update — show highlight immediately, rollback on error
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<HighlightResponse[]>(key);
      const tempId = `${OPTIMISTIC_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimistic: HighlightResponse = {
        id: tempId,
        text: input.text,
        color: input.color,
        startOffset: input.startOffset,
        endOffset: input.endOffset,
        ...(input.note ? { note: input.note } : {}),
        ...(input.prefix ? { prefix: input.prefix } : {}),
        ...(input.suffix ? { suffix: input.suffix } : {}),
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<HighlightResponse[]>(key, (old) => [...(old ?? []), optimistic]);
      return { prev, tempId };
    },
    onError: async (err, input, ctx) => {
      if (isOfflineError(err)) {
        // Keep the optimistic entry: the create is queued and replayed on
        // reconnect (vault:hl-confirmed swaps the temp id for the real one).
        await enqueueCourseCreate({ courseId, lessonNum, input }, ctx?.tempId ?? '', ownerId);
        if (ctx) ctx.queuedOffline = true;
        return;
      }
      if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev);
    },
    onSettled: (_data, _err, _vars, ctx) => {
      // While queued offline, skip the refetch — it would wipe the optimistic entry
      if (ctx?.queuedOffline) return;
      qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUpdateHighlight(courseId: string, lessonNum: number) {
  const qc = useQueryClient();
  const ownerId = useRef(auth.getUserId()).current;
  const key = COURSE_KEYS.highlights(courseId, lessonNum);
  return useMutation<
    HighlightResponse,
    Error,
    { id: string; patch: HighlightUpdateInput },
    HighlightMutationCtx
  >({
    mutationFn: ({ id, patch }) => {
      if (!ownerId || auth.getUserId() !== ownerId) throw new Error('La sesión cambió');
      return courseService.updateHighlight(courseId, lessonNum, id, patch, ownerId);
    },
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<HighlightResponse[]>(key);
      qc.setQueryData<HighlightResponse[]>(key, (old) =>
        (old ?? []).map((h) => {
          if (h.id !== id) return h;
          const next = { ...h };
          if (patch.color) next.color = patch.color;
          if (patch.note !== undefined) {
            // '' removes the note (API contract)
            if (patch.note === '') delete next.note;
            else next.note = patch.note;
          }
          return next;
        }),
      );
      return { prev };
    },
    onError: async (err, { id, patch }, ctx) => {
      if (isOfflineError(err)) {
        // Keep the optimistic patch: replayed on reconnect
        await enqueueCourseUpdate(courseHighlightRef(courseId, lessonNum, id), patch, ownerId);
        if (ctx) ctx.queuedOffline = true;
        return;
      }
      if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev);
    },
    onSettled: (_data, _err, _vars, ctx) => {
      if (ctx?.queuedOffline) return;
      qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useDeleteHighlight(courseId: string, lessonNum: number) {
  const qc = useQueryClient();
  const ownerId = useRef(auth.getUserId()).current;
  const key = COURSE_KEYS.highlights(courseId, lessonNum);

  // Undoable delete ("delayed commit"): the item leaves the cache NOW and a
  // toast offers Deshacer for 5s; only then the DELETE fires. Undo (or a
  // failed commit) puts the item back.
  const mutate = useCallback(
    (id: string) => {
      void qc.cancelQueries({ queryKey: key });
      const item = qc.getQueryData<HighlightResponse[]>(key)?.find((h) => h.id === id);
      if (!item) return;

      qc.setQueryData<HighlightResponse[]>(key, (old) => (old ?? []).filter((h) => h.id !== id));

      scheduleUndoableDelete({
        message: 'Subrayado eliminado',
        onCommit: async () => {
          // A highlight created offline was never synced: deleting it cancels
          // its pending create instead (temp ids are not valid server ids).
          if (id.startsWith(OPTIMISTIC_ID_PREFIX)) {
            await enqueueCourseDelete(id, ownerId);
            return;
          }
          try {
            if (!ownerId || auth.getUserId() !== ownerId) throw new Error('La sesión cambió');
            await courseService.deleteHighlight(courseId, lessonNum, id, ownerId);
            void qc.invalidateQueries({ queryKey: key });
          } catch (err) {
            if (isOfflineError(err)) {
              await enqueueCourseDelete(courseHighlightRef(courseId, lessonNum, id), ownerId);
              return; // queued — keep it removed
            }
            throw err; // scheduleUndoableDelete restores + shows an error toast
          }
        },
        onRestore: () => {
          qc.setQueryData<HighlightResponse[]>(key, (old = []) => {
            if (old.some((h) => h.id === item.id)) return old;
            return [...old, item].sort((a, b) => (a.startOffset ?? 0) - (b.startOffset ?? 0));
          });
        },
      });
    },
    [qc, key, courseId, lessonNum, ownerId],
  );

  return { mutate };
}
